import { BillingCycle, WorkspacePlan } from '@up2stay/up2promo-api-client';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface PendingUpgradeEntry {
  plan: WorkspacePlan;
  billingCycle: BillingCycle;
}

interface PendingUpgradeState {
  byWorkspaceId: Record<string, PendingUpgradeEntry>;
  setPending: (
    workspaceId: string,
    plan: WorkspacePlan,
    billingCycle: BillingCycle,
  ) => void;
  clearPending: (workspaceId: string) => void;
  getPending: (workspaceId: string) => PendingUpgradeEntry | undefined;
}

const WORKSPACE_PLANS = new Set<string>(Object.values(WorkspacePlan));
const BILLING_CYCLES = new Set<string>(Object.values(BillingCycle));

function parseWorkspacePlan(value: unknown): WorkspacePlan | undefined {
  if (typeof value !== 'string') return undefined;
  if (!WORKSPACE_PLANS.has(value)) return undefined;
  return value as WorkspacePlan;
}

function parseBillingCycle(value: unknown): BillingCycle {
  if (typeof value === 'string' && BILLING_CYCLES.has(value)) {
    return value as BillingCycle;
  }
  return BillingCycle.MONTHLY;
}

function normalizeEntry(value: unknown): PendingUpgradeEntry | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const plan = parseWorkspacePlan(value);
    if (!plan) return undefined;
    return { plan, billingCycle: BillingCycle.MONTHLY };
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'plan' in value &&
    'billingCycle' in value
  ) {
    const plan = parseWorkspacePlan((value as { plan: unknown }).plan);
    if (!plan) return undefined;
    return {
      plan,
      billingCycle: parseBillingCycle(
        (value as { billingCycle: unknown }).billingCycle,
      ),
    };
  }
  return undefined;
}

export const usePendingUpgradeStore = create<PendingUpgradeState>()(
  persist(
    (set, get) => ({
      byWorkspaceId: {},
      setPending: (workspaceId, plan, billingCycle) =>
        set((state) => ({
          byWorkspaceId: {
            ...state.byWorkspaceId,
            [workspaceId]: { plan, billingCycle },
          },
        })),
      clearPending: (workspaceId) =>
        set((state) => {
          const next = { ...state.byWorkspaceId };
          delete next[workspaceId];
          return { byWorkspaceId: next };
        }),
      getPending: (workspaceId) => {
        const raw = get().byWorkspaceId[workspaceId];
        return normalizeEntry(raw);
      },
    }),
    {
      name: 'pending-upgrade-store',
      merge: (persisted, current) => {
        const persistedState = persisted as
          | Partial<PendingUpgradeState>
          | undefined;
        const normalized: Record<string, PendingUpgradeEntry> = {};
        for (const [workspaceId, value] of Object.entries(
          persistedState?.byWorkspaceId ?? {},
        )) {
          const entry = normalizeEntry(value);
          if (entry) normalized[workspaceId] = entry;
        }
        return {
          ...current,
          ...persistedState,
          byWorkspaceId: normalized,
        };
      },
    },
  ),
);
